// src/hooks/useSiteEditor.js - Hook replacement that uses ClientSiteStore
import useClientSiteStore from '../stores/ClientSiteStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing client site data from ClientSiteStore
 *
 * Uses Zustand ClientSiteStore instead of local state.
 * Store initialization is handled at app level in SocketContext.
 *
 * @returns {object} Client site data and operations
 */
export const useSiteEditor = () => {
  const socket = useSocket();

  // Use individual selectors instead of destructuring entire store
  // This ensures React re-renders when specific properties change

  // Site list data
  const sites = useClientSiteStore(state => state.sites);
  const sitesLoading = useClientSiteStore(state => state.sitesLoading);

  // Selected site data
  const selectedSite = useClientSiteStore(state => state.selectedSite);
  const selectedSiteLoading = useClientSiteStore(state => state.selectedSiteLoading);

  // Operation states
  const savingDraft = useClientSiteStore(state => state.savingDraft);
  const publishing = useClientSiteStore(state => state.publishing);

  // Connection state
  const isConnected = useClientSiteStore(state => state.isConnected);

  // Internal initialization state (for fallback check)
  const _initialized = useClientSiteStore(state => state._initialized);

  // Editing state
  const editing = useClientSiteStore(state => state.editing);

  // Site operations (functions don't change, so we can get them once)
  const loadSites = useClientSiteStore(state => state.loadSites);
  const loadSiteDetails = useClientSiteStore(state => state.loadSiteDetails);
  const updateDraft = useClientSiteStore(state => state.updateDraft);
  const publishSite = useClientSiteStore(state => state.publishSite);

  // Editing operations (new pattern)
  const startEditing = useClientSiteStore(state => state.startEditing);
  const updateEditingDraft = useClientSiteStore(state => state.updateEditingDraft);
  const stopEditing = useClientSiteStore(state => state.stopEditing);

  // Utility functions
  const getSiteById = useClientSiteStore(state => state.getSiteById);
  const clearSelectedSite = useClientSiteStore(state => state.clearSelectedSite);
  const refreshSites = useClientSiteStore(state => state.refreshSites);

  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useSiteEditor: Store not initialized at app level, trying fallback');
    socket.initializeStore('ClientSiteStore');
  }

  // Return the interface
  return {
    // Site list data
    sites,
    sitesLoading,

    // Selected site data (current editing site)
    currentSite: selectedSite,
    selectedSite,
    selectedSiteLoading,

    // Draft content (convenience accessor - now from editing state)
    draftContent: editing.draft || selectedSite?.draft_configuration || null,

    // Editing state (new pattern)
    editing,
    isDirty: editing.isDirty,

    // Operation states
    savingDraft,
    saving: savingDraft, // Alias for compatibility
    publishing,

    // Computed properties
    hasUnpublishedChanges: selectedSite?.has_unpublished_changes || false,

    // Site operations (legacy)
    loadSites,
    loadSiteDetails,
    updateDraft,
    publishSite,

    // Editing operations (new pattern)
    startEditing,
    updateEditingDraft,
    stopEditing,

    // Utility functions
    getSiteById,
    clearSelectedSite,
    refreshSites,

    // Connection state
    isConnected: socket.isAuthenticated,

    // Loading state (combines both loading states)
    loading: sitesLoading || selectedSiteLoading,

    // Error state (sites don't have errors in the store, but we keep for compatibility)
    error: null
  };
};
